import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Tag,
  Tooltip,
  Button,
  Modal,
  Input,
  Pagination,
  message,
  Typography,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  getRequest,
  postRequest,
  putRequest,
} from "../../util/service";
import dayjs from "dayjs";
import MultiSelectPeoplePicker from "../shared/MultiSelectPeoplePicker/MultiSelectPeoplePicker";
import UserDetails from "../userDetails/UserDetails";
import { WELCOME_TEXT_HTML } from "../../constants/labels";

const { Text } = Typography;

export default function CreateProject() {
    const userName = useSelector((s) => s.header.userName);
    const userEmail = useSelector((s) => s.header.userEmail);
    // FIX: Get the authentication readiness state from Redux
    const isAuthReady = useSelector((s) => s.header.isAuthReady);

    // --- State Management ---
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editProjectId, setEditProjectId] = useState(null);
    const [projectName, setProjectName] = useState("");
    const [description, setDescription] = useState("");
    const [pOwnerData, setPOwnerData] = useState([]);
    const [teamMembersData, setTeamMembersData] = useState([]);
    const [projectStatus, setProjectStatus] = useState("active");
    const [savingProject, setSavingProject] = useState(false);
    const [dialogInitialValues, setDialogInitialValues] = useState(null);
    
    // --- Pagination & Search State ---
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [searchText, setSearchText] = useState("");

    // --- Data Fetching ---
  const fetchProjects = useCallback(async () => {
    if (!userName) return;
    setLoading(true);
    try {
      const resp = await getRequest(`/project/user/${userName}`);
      const sortedProjects = (resp?.data || []).slice().sort(
        (a, b) => new Date(b.created_ts) - new Date(a.created_ts)
      );
      setProjects(sortedProjects);
    } catch {
      message.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, [userName]);

    // FIX: The data fetch is now dependent on isAuthReady
    useEffect(() => {
        // Only fetch projects if authentication is ready and the user is logged in.
        if (isAuthReady && userName) {
            fetchProjects();
        }
    }, [isAuthReady, userName, fetchProjects]); // Add isAuthReady to the dependency array

    // --- Memoized Data for Display ---
    const filteredProjects = useMemo(() => {
        return projects.filter(proj => {
            const lowerSearchText = searchText.toLowerCase();
            if (!lowerSearchText) return true;

            const nameMatch = proj.p_name.toLowerCase().includes(lowerSearchText);
            const descMatch = proj.p_description.toLowerCase().includes(lowerSearchText);
            const ownerMatch = proj.p_owner.some(owner => owner.toLowerCase().includes(lowerSearchText));
            
            return nameMatch || descMatch || ownerMatch;
        });
    }, [projects, searchText]);

    const displayedProjects = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredProjects.slice(start, start + rowsPerPage);
    }, [filteredProjects, page, rowsPerPage]);

    // --- Dialog Handlers ---
    const applyDialogValues = (values) => {
        setProjectName(values.projectName);
        setDescription(values.description);
        setPOwnerData(values.pOwnerData.map((owner) => ({ ...owner })));
        setTeamMembersData(values.teamMembersData.map((member) => ({ ...member })));
        setProjectStatus(values.projectStatus);
    };

    const handleOpenDialog = () => {
        const initialValues = {
            projectName: "",
            description: "",
            pOwnerData: [{ name: userName, email_id: userEmail }],
            teamMembersData: [],
            projectStatus: "active",
        };

        setIsEditMode(false);
        setEditProjectId(null);
        setDialogInitialValues(initialValues);
        applyDialogValues(initialValues);
        setOpenDialog(true);
    };
    
    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSavingProject(false);
    };

    const handleResetDialog = () => {
        if (!dialogInitialValues) return;
        applyDialogValues(dialogInitialValues);
    };

    // --- CRUD Operations ---
    const handleSaveProject = async () => {
        if (savingProject) return;
        const owners = pOwnerData.map((o) => o.name);
        const team = teamMembersData.map((t) => ({
            username: t.name,
            email: t.email_id,
        }));
        
        pOwnerData.forEach((owner) => {
            if (!team.some((t) => t.email === owner.email_id)) {
                team.push({ username: owner.name, email: owner.email_id });
            }
        });

        const payload = {
            p_name: projectName,
            p_description: description,
            p_owner: owners,
            team_members: team,
            p_status: projectStatus,
        };

        try {
            setSavingProject(true);
            if (isEditMode) {
                await putRequest(`/project/data/${editProjectId}`, payload);
                message.success("Project updated");
            } else {
                await postRequest("/project/data", payload);
                message.success("Project created");
            }
            setOpenDialog(false);
            fetchProjects();
        } catch {
            message.error("Error saving project");
        } finally {
            setSavingProject(false);
        }
    };

    const handleEditProject = (proj) => {
        const ownerData = proj.p_owner.map(ownerName => {
            const ownerDetails = proj.team_members.find(member => member.username === ownerName);
            return { name: ownerName, email_id: ownerDetails?.email || "" };
        });
        const filteredTeam = (proj.team_members || []).filter(
            (m) => !proj.p_owner.includes(m.username)
        );
        const initialValues = {
            projectName: proj.p_name,
            description: proj.p_description,
            projectStatus: proj.p_status || "active",
            pOwnerData: ownerData,
            teamMembersData: filteredTeam.map((m) => ({ name: m.username, email_id: m.email })),
        };

        setIsEditMode(true);
        setEditProjectId(proj.p_record_id);
        setDialogInitialValues(initialValues);
        applyDialogValues(initialValues);
        setOpenDialog(true);
    };

    const isFormValid =
        projectName.trim().length > 0 &&
        description.trim().length > 0 &&
        pOwnerData.length > 0;

    // --- Table Columns ---
    const columns = [
        {
            title: "Name",
            dataIndex: "p_name",
            width: "15%",
            sorter: (a, b) => a.p_name.localeCompare(b.p_name),
            render: (text, record) => (
                <Link
                    to={`/${record.p_record_id}/result`}
                    state={{
                        projectName: record.p_name,
                        owners: record.p_owner,
                        teamMembers: record.team_members.map((m) => m.username),
                        description: record.p_description,
                    }}
                >
                    {text}
                </Link>
            ),
        },
         {
            title: "Description",
            dataIndex: "p_description",
            width: "25%",
            render: (desc) => (
                <Tooltip title={desc}>
                    <div style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}>
                        {desc}
                    </div>
                </Tooltip>
            ),
        },
        {
            title: "Owners",
            dataIndex: "p_owner",
            width: "20%",
            render: (owners) => {
                const names = owners || [];
                if (names.length <= 2) return names.map((n) => <Tag key={n}>{n}</Tag>);
                const firstTwo = names.slice(0, 2);
                const more = names.slice(2).join(", ");
                return <><Tooltip title={more}>{firstTwo.map((n) => <Tag key={n}>{n}</Tag>)}<Tag>+{names.length - 2} more</Tag></Tooltip></>;
            },
        },
        {
            title: "Created",
            dataIndex: "created_ts",
            width: "15%",
            sorter: (a, b) => dayjs(a.created_ts).unix() - dayjs(b.created_ts).unix(),
            render: (ts) => (ts ? dayjs(ts).format("MMM D, YYYY h:mm A") : ""),
        },
        {
            title: <div style={{ textAlign: "center" }}>Edit</div>,
            key: "actions",
            width: "5%",
            render: (_, record) => {
                const isOwner = (record.p_owner || []).includes(userName);
                return isOwner ? <div style={{ textAlign: "center" }}><Button icon={<EditOutlined />} size="small" onClick={() => handleEditProject(record)} /></div> : null;
            },
        },
    ];

    return (
        <div style={{ padding: "0 24px" }}>
            <div style={{ margin: "16px 0" }}><UserDetails /></div>

            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col><h2>Projects</h2></Col>
                <Col><Button type="primary" icon={<PlusOutlined />} onClick={handleOpenDialog}>Create Project</Button></Col>
            </Row>
            
            <Row style={{ marginBottom: 16 }}>
                <Col span={8}>
                    <Input.Search
                        placeholder="Search by Project, Owner, or Description"
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        allowClear
                    />
                </Col>
            </Row>

            <Card>
                <Table
                    bordered
                    rowKey="p_record_id"
                    dataSource={displayedProjects}
                    columns={columns}
                    pagination={false}
                    loading={loading}
                />
                {filteredProjects.length > rowsPerPage && (
                    <Row justify="end" style={{ marginTop: 16 }}>
                        <Pagination
                            current={page + 1}
                            pageSize={rowsPerPage}
                            total={filteredProjects.length}
                            showSizeChanger
                            pageSizeOptions={["5", "10", "25"]}
                            onChange={(p, size) => {
                                setPage(p - 1);
                                setRowsPerPage(size);
                            }}
                        />
                    </Row>
                )}
            </Card>

            <div style={{ marginTop: 24, padding: 16, background: "#fafafa", borderRadius: 4 }}
                dangerouslySetInnerHTML={{ __html: WELCOME_TEXT_HTML }}
            />

              <Modal
                title={isEditMode ? "Edit Project" : "Create Project"}
                open={openDialog}
                onCancel={handleCloseDialog}
                maskClosable={false}
                keyboard={false}
                closeIcon={<CloseOutlined style={{ color: "#ff4d4f" }} />}
                footer={[
                    <Button key="reset" onClick={handleResetDialog}>
                        Reset
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={handleSaveProject}
                        disabled={!isFormValid || savingProject}
                        loading={savingProject}
                    >
                        {isEditMode ? "Save" : "Create"}
                    </Button>,
                ]}
                destroyOnClose
              >
                <div style={{ marginBottom: 16 }}>
                    <label>Name&nbsp;<span style={{ color: "red" }}>*</span></label>
                    <Input value={projectName} onChange={(e) => setProjectName(e.target.value)} disabled={isEditMode} placeholder="Project Name" maxLength={50} showCount />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label>Description&nbsp;<span style={{ color: "red" }}>*</span></label>
                    <Input.TextArea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={300} showCount placeholder="Short description" />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label>Owners&nbsp;<span style={{ color: "red" }}>*</span></label>
                    <MultiSelectPeoplePicker 
                        selectedUsersData={pOwnerData} 
                        onChange={setPOwnerData} 
                        selectionLimit={2}
                        currentUserEmail={userEmail}
                    />
                    {pOwnerData.length >= 2 && (
                        <Text type="warning" style={{ fontSize: "12px" }}>The max owner can be 2 for the project.</Text>
                    )}
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label>Team Members</label>
                    <MultiSelectPeoplePicker 
                        selectedUsersData={teamMembersData} 
                        onChange={setTeamMembersData} 
                        selectionLimit={10}
                        disabledOptions={pOwnerData.map(owner => owner.email_id)}
                    />
                    {teamMembersData.length >= 10 && (
                        <Text type="warning" style={{ fontSize: "12px" }}>The max team members can be 10 for the project (exclusive of owners).</Text>
                    )}
                </div>
            </Modal>
        </div>
    );
}
